/**
 * Die Passwortregel – eine einzige, und der Server entscheidet.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum hier und nicht im Server
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Die Regel steht in `packages/shared`, damit die Oberfläche beim Tippen
 * dasselbe sagen kann wie der Server beim Speichern. Das ist Bequemlichkeit,
 * keine Entscheidung: Der Browser darf die Regel ANZEIGEN, er darf sie
 * niemals ANWENDEN. Jede Stelle, die ein Passwort entgegennimmt, ruft
 * `pruefePasswort()` noch einmal auf dem Server – siehe
 * `apps/server/src/services/passwort.ts`.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was die Regel NICHT verlangt
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Kein «ein Grossbuchstabe, eine Zahl, ein Sonderzeichen». Diese Vorgabe hat
 * eine messbare Wirkung, und zwar die falsche: Sie erzeugt `Sommer2026!` und
 * verbietet `der hund frisst keine zwiebeln`. Das zweite ist um Grössen-
 * ordnungen besser und fiele durch jede Musterprüfung.
 *
 * Deshalb: Länge als Grundlage, Leerzeichen und Passphrasen ausdrücklich
 * erlaubt, Unicode nicht künstlich verboten – und statt Musterpflicht eine
 * Liste dessen, was nachweislich als Erstes probiert wird.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Die Fehlerrichtung
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Diese Prüfung soll Triviales abweisen, nicht Gutes aussortieren. Wo eine
 * Regel unsicher ist, lässt sie durch. Ein mittelmässiges Passwort, das
 * angenommen wird, ist ein kleinerer Schaden als ein gutes, das abgelehnt
 * wird – denn wer dreimal abgewiesen wird, nimmt beim vierten Versuch etwas,
 * das er sich merken kann, und das ist dann `Sommer2026!`.
 */

/** Kürzer geht nicht. Zwölf Zeichen sind die Grundlage, auf der alles andere steht. */
export const PASSWORT_MIN = 12

/**
 * Und länger auch nicht.
 *
 * Nicht aus Geschmack: Jede Prüfung rechnet scrypt mit 32 MB Arbeitsspeicher.
 * Ein Passwortfeld ohne Obergrenze ist eine Einladung, den Server mit sehr
 * langen Eingaben zu beschäftigen. 128 Zeichen sind mehr, als jede Passphrase
 * braucht.
 */
export const PASSWORT_MAX = 128

export type Passwortgrund =
  | 'ZU_KURZ'
  | 'ZU_LANG'
  | 'NUR_ZIFFERN'
  | 'ZU_WENIG_VERSCHIEDEN'
  | 'WIEDERHOLUNG'
  | 'SEQUENZ'
  | 'TASTATURMUSTER'
  | 'TRIVIAL'
  | 'ENTHAELT_MAIL'
  | 'ENTHAELT_NAME'

export interface Passwortbefund {
  ok: boolean
  grund: Passwortgrund | null
  /** Ein Satz für die Person davor – kein Regelname, keine Fehlernummer. */
  text: string
}

export interface Passwortumfeld {
  /** Die Mailadresse des Zugangs – sie darf nicht im Passwort stehen. */
  email?: string | null
  /** Der Name des Betriebs – derselbe Grund. */
  betrieb?: string | null
  /** Der Name der Person, falls bekannt. */
  name?: string | null
}

const GUT: Passwortbefund = { ok: true, grund: null, text: '' }

function nein(grund: Passwortgrund, text: string): Passwortbefund {
  return { ok: false, grund, text }
}

/**
 * Für den Vergleich wird kleingeschrieben, zusammengezogen und entakzentuiert.
 *
 * `Sömmer` und `sommer` sind für einen Angreifer dasselbe Wort; für diese
 * Prüfung müssen sie es auch sein. NFKD plus das Entfernen der kombinierenden
 * Zeichen ist der Weg, der ohne Tabelle auskommt.
 */
function normal(w: string): string {
  return w
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
}

/**
 * Was Angreifer zuerst probieren.
 *
 * Die Liste ist bewusst kurz und deutschsprachig gewichtet. Sie ersetzt keine
 * Prüfung gegen eine Leak-Datenbank – die wäre besser und braucht einen
 * Dienst. Sie fängt das ab, was ohne jede Datenbank in den ersten hundert
 * Versuchen drankommt.
 */
const TRIVIALE_WOERTER = [
  'passwort',
  'password',
  'kennwort',
  'geheim',
  'secret',
  'admin',
  'administrator',
  'benutzer',
  'user',
  'login',
  'willkommen',
  'welcome',
  'letmein',
  'iloveyou',
  'monkey',
  'dragon',
  'master',
  'sunshine',
  'princess',
  'football',
  'baseball',
  'starwars',
  'hallo',
  'servus',
  'gruezi',
  'sommer',
  'winter',
  'fruehling',
  'herbst',
  'januar',
  'februar',
  'maerz',
  'april',
  'august',
  'september',
  'oktober',
  'november',
  'dezember',
  'schweiz',
  'zuerich',
  'basel',
  'bern',
  'raporto',
  'handwerk',
  'handwerker',
  'monteur',
  'baustelle',
  'rechnung',
  'offerte',
  'rapport',
  'firma',
  'betrieb',
  'test',
  'demo',
  'probe',
  'start',
  'change',
  'changeme',
  'qwertz',
  'qwerty',
  'asdfgh'
]

/** Tastaturreihen, deutsch und englisch, jeweils auch rückwärts geprüft. */
const TASTATURREIHEN = [
  'qwertzuiopue',
  'qwertyuiop',
  'asdfghjkloeae',
  'asdfghjkl',
  'yxcvbnm',
  'zxcvbnm',
  '1234567890',
  '0987654321',
  '!"§$%&/()='
]

/** Die längste Folge, in der jedes Zeichen genau eins über oder unter dem vorigen liegt. */
function laengsteFolge(w: string): number {
  if (w.length < 2) return w.length
  let best = 1
  let lauf = 1
  let richtung = 0
  for (let i = 1; i < w.length; i++) {
    const d = w.charCodeAt(i) - w.charCodeAt(i - 1)
    if (d === 1 || d === -1) {
      if (d === richtung) {
        lauf++
      } else {
        richtung = d
        lauf = 2
      }
      if (lauf > best) best = lauf
    } else {
      richtung = 0
      lauf = 1
    }
  }
  return best
}

/** Besteht das Passwort aus einem kurzen Block, der sich wiederholt? */
function istWiederholung(w: string): boolean {
  for (let len = 1; len <= 4 && len * 2 <= w.length; len++) {
    if (w.length % len !== 0) continue
    const block = w.slice(0, len)
    if (block.repeat(w.length / len) === w) return true
  }
  return false
}

/** Ein zusammenhängendes Stück aus einer Tastaturreihe, mindestens sechs Zeichen. */
function tastaturmuster(w: string): boolean {
  for (const reihe of TASTATURREIHEN) {
    const rueckwaerts = [...reihe].reverse().join('')
    for (const quelle of [reihe, rueckwaerts]) {
      for (let i = 0; i + 6 <= quelle.length; i++) {
        if (w.includes(quelle.slice(i, i + 6))) return true
      }
    }
  }
  return false
}

/**
 * Die Wortteile, die aus einem Umfeldwert verboten sind.
 *
 * Aus «AV Parkett GmbH» werden `parkett` und `gmbh`; Rechtsformen und sehr
 * kurze Teile fallen weg, weil sonst jedes Passwort mit «ag» darin scheitern
 * würde.
 */
const RECHTSFORMEN = new Set(['gmbh', 'ag', 'sa', 'sarl', 'kg', 'ohg', 'eg', 'und', 'der', 'die', 'das'])

function teileAus(wert: string | null | undefined): string[] {
  if (!wert) return []
  return normal(wert)
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 4 && !RECHTSFORMEN.has(t))
}

/**
 * Die Prüfung.
 *
 * Reihenfolge mit Absicht: zuerst das, was die Person sofort selbst sieht
 * (Länge), dann das, was sie erklärt bekommen muss.
 */
export function pruefePasswort(passwort: string, umfeld: Passwortumfeld = {}): Passwortbefund {
  /* Länge zählt Codepunkte, nicht UTF-16-Einheiten: ein Emoji ist ein
     Zeichen und nicht zwei. Wer eine Passphrase mit Zeichen ausserhalb der
     Grundebene baut, soll nicht stillschweigend doppelt gezählt werden. */
  const zeichen = [...passwort]

  if (zeichen.length < PASSWORT_MIN) {
    return nein(
      'ZU_KURZ',
      `Das Passwort braucht mindestens ${PASSWORT_MIN} Zeichen. Ein ganzer Satz ist leichter zu merken als ein kurzes Kunstwort – «der hund frisst keine zwiebeln» ist ein gutes Passwort.`
    )
  }
  if (zeichen.length > PASSWORT_MAX) {
    return nein(
      'ZU_LANG',
      `Das Passwort darf höchstens ${PASSWORT_MAX} Zeichen haben.`
    )
  }

  const n = normal(passwort)
  const ohneLeer = n.replace(/\s+/g, '')

  if (/^[0-9\s]+$/.test(passwort)) {
    return nein(
      'NUR_ZIFFERN',
      'Eine reine Zahlenfolge ist zu leicht zu erraten. Nimm Wörter dazu.'
    )
  }

  if (istWiederholung(ohneLeer)) {
    return nein(
      'WIEDERHOLUNG',
      'Das Passwort wiederholt immer dasselbe Stück. Das zählt wie ein sehr kurzes Passwort.'
    )
  }

  if (new Set(ohneLeer).size <= 4) {
    return nein(
      'ZU_WENIG_VERSCHIEDEN',
      'Das Passwort besteht aus zu wenigen verschiedenen Zeichen.'
    )
  }

  if (laengsteFolge(ohneLeer) >= 6) {
    return nein(
      'SEQUENZ',
      'Das Passwort enthält eine durchgehende Reihe wie «123456» oder «abcdef». Solche Reihen werden zuerst probiert.'
    )
  }

  if (tastaturmuster(ohneLeer)) {
    return nein(
      'TASTATURMUSTER',
      'Das Passwort folgt einer Tastaturreihe. Solche Muster stehen in jeder Angriffsliste.'
    )
  }

  /*
    Ein triviales Wort ist nicht per se verboten – «sommer» darf in einer
    Passphrase vorkommen. Verboten ist es, wenn kaum etwas anderes da ist:
    Was nach dem Entfernen des Worts übrig bleibt, muss für sich allein noch
    ein halbwegs eigenständiges Passwort sein.
  */
  const nurBuchstabenZahlen = ohneLeer.replace(/[^a-z0-9]/g, '')
  for (const wort of TRIVIALE_WOERTER) {
    if (!nurBuchstabenZahlen.includes(wort)) continue
    const rest = nurBuchstabenZahlen.split(wort).join('')
    if (rest.length < 8) {
      return nein(
        'TRIVIAL',
        'Dieses Passwort ist zu nah an einem Wort, das in jeder Angriffsliste steht. Eine angehängte Zahl ändert daran wenig.'
      )
    }
  }

  /*
    Mailadresse und Betriebsname.

    Geprüft wird gegen denselben Massstab: Das Passwort darf das Wort
    enthalten, aber nicht im Wesentlichen daraus bestehen.
  */
  const mail = normal(umfeld.email ?? '')
  const lokal = mail.split('@')[0] ?? ''
  const domain = (mail.split('@')[1] ?? '').split('.')[0] ?? ''

  for (const teil of [mail, lokal, domain].filter((t) => t.length >= 3)) {
    if (!nurBuchstabenZahlen.includes(teil.replace(/[^a-z0-9]/g, ''))) continue
    const rest = nurBuchstabenZahlen.split(teil.replace(/[^a-z0-9]/g, '')).join('')
    if (rest.length < 8) {
      return nein(
        'ENTHAELT_MAIL',
        'Das Passwort besteht im Wesentlichen aus deiner Mailadresse. Wer die Adresse kennt, kennt dann auch das Passwort.'
      )
    }
  }

  for (const teil of [...teileAus(umfeld.betrieb), ...teileAus(umfeld.name)]) {
    if (!nurBuchstabenZahlen.includes(teil)) continue
    const rest = nurBuchstabenZahlen.split(teil).join('')
    if (rest.length < 8) {
      return nein(
        'ENTHAELT_NAME',
        'Das Passwort besteht im Wesentlichen aus dem Namen deines Betriebs. Den kennt jeder Kunde.'
      )
    }
  }

  return GUT
}

/* ═══════════════════════════════════════════════════════════════════════════
   Ein Passwort erzeugen
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Woher die Zufallsbytes kommen.
 *
 * Im Browser `crypto.getRandomValues`, im Server `randomBytes`. Beide sind
 * kryptografisch; `Math.random()` ist es nicht und darf hier nie stehen –
 * ein daraus erzeugtes Passwort ist aus wenigen Beobachtungen vorhersagbar.
 * Die Quelle wird deshalb hereingereicht und nicht hier gewählt: So kann
 * diese Datei in beiden Welten liegen, ohne dass eine von beiden einen
 * schlechteren Zufall mitbringt.
 */
export type Zufallsquelle = (anzahl: number) => Uint8Array

/**
 * Das Alphabet.
 *
 * Ohne `I`, `l`, `1`, `O` und `0`: Dieses Passwort wird abgeschrieben – vom
 * Bildschirm auf einen Zettel, vom Zettel ins Handy. Ein `O`, das eine Null
 * sein könnte, kostet einen Anruf. Keine Sonderzeichen, weil sie beim
 * Weitergeben über Chat, Shell und Mail mehr Ärger machen als Entropie
 * bringen: 58 Zeichen auf 22 Stellen sind rund 129 Bit, und das ist weit
 * jenseits dessen, was jemand durchprobiert.
 */
const ALPHABET = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'

/** Wie lang ein erzeugtes Passwort ist – Vorgabe in der Mitte des Rahmens 20–24. */
export const ERZEUGTE_LAENGE = 22

/**
 * Ein starkes Passwort, das die Regel garantiert erfüllt.
 *
 * Zwei Dinge sind hier bewusst gemacht:
 *
 * **Keine Modulo-Verzerrung.** `byte % 58` bevorzugt die ersten Zeichen des
 * Alphabets, weil 256 kein Vielfaches von 58 ist. Der Unterschied ist klein
 * und trotzdem vermeidbar: Bytes oberhalb der letzten vollen Runde werden
 * verworfen und neu gezogen.
 *
 * **Die Regel wird nachgeprüft, nicht angenommen.** Ein zufälliges Passwort
 * kann rein rechnerisch eine Folge wie «mnopqr» enthalten, und die lehnt
 * `pruefePasswort()` ab. Ohne diese Schleife bekäme jemand ein Passwort
 * vorgeschlagen, das der Server danach verweigert – einmal unter hundert-
 * tausend Malen, und genau das ist der Fehler, den niemand reproduzieren
 * kann. Die Schleife ist die Zusicherung, dass hier nur herauskommt, was
 * auch angenommen wird.
 */
export function erzeugeStarkesPasswort(
  zufall: Zufallsquelle,
  laenge: number = ERZEUGTE_LAENGE
): string {
  const grenze = Math.floor(256 / ALPHABET.length) * ALPHABET.length

  for (let versuch = 0; versuch < 100; versuch++) {
    let wort = ''
    while (wort.length < laenge) {
      const bytes = zufall(laenge * 2)
      for (const b of bytes) {
        if (b >= grenze) continue
        wort += ALPHABET[b % ALPHABET.length]
        if (wort.length === laenge) break
      }
    }
    if (pruefePasswort(wort).ok) return wort
  }

  /* Unerreichbar, solange das Alphabet gross genug ist – aber lieber ein
     lauter Fehler als ein stilles, schwächeres Passwort. */
  throw new Error('Es liess sich kein Passwort erzeugen, das der Regel genügt.')
}

/** Für die Oberfläche: die Regel in einem Satz, ohne Fehlerfall. */
export const PASSWORT_HINWEIS =
  `Mindestens ${PASSWORT_MIN} Zeichen. Ein ganzer Satz ist erlaubt und meist die beste Wahl. ` +
  'Keine Vorgabe zu Grossbuchstaben, Zahlen oder Sonderzeichen.'

/**
 * Wie lange ein abgelegtes Passwort gesperrt bleibt.
 *
 * Zwölf Monate sind eine Entscheidung des Gründers und keine technische
 * Grösse. Sie steht hier, weil Server und Oberfläche denselben Satz sagen
 * müssen.
 */
export const PASSWORT_HISTORIE_MONATE = 12
